import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import type { Feature, FeatureCollection } from 'geojson';
import { useTheme } from '@/theme';
import { Caption } from '@/components/ui/Text';
import { isMapboxTokenConfigured } from '@/config/mapbox';
import { countryDisplayNameToIso2 } from '@/utils/countryNameToIso2';
import { ISO2_TO_ISO3 } from '@/utils/iso2ToIso3';
import { geoFeatureCollectionBBox } from '@/utils/geoFeatureCollectionBBox';
import type { RNMapboxCamera } from '@/types/mapbox';

// eslint-disable-next-line @typescript-eslint/no-require-imports
const WORLD_GEO = require('../../../assets/geo/world-countries.json') as FeatureCollection;

const MAX_CITY_PINS = 18;
const GEOCODE_STAGGER_MS = 120;

export type CityPinInput = {
  key: string;
  city: string;
  country: string;
};

let Mapbox: any = null;
let Camera: React.ComponentType<any> | null = null;
let ShapeSource: React.ComponentType<any> | null = null;
let FillLayer: React.ComponentType<any> | null = null;
let CircleLayer: React.ComponentType<any> | null = null;
let MAPBOX_STYLE_URL = '';

if (Platform.OS !== 'web') {
  try {
    const mapboxModule = require('@rnmapbox/maps');
    Mapbox = mapboxModule.default;
    Camera = mapboxModule.Camera;
    ShapeSource = mapboxModule.ShapeSource;
    FillLayer = mapboxModule.FillLayer;
    CircleLayer = mapboxModule.CircleLayer;
    const { MAPBOX_STYLE_URL: styleUrl } = require('@/config/mapbox');
    MAPBOX_STYLE_URL = styleUrl;
    const { getMapboxAccessTokenSync } = require('@/config/mapbox');
    const token = getMapboxAccessTokenSync();
    if (Mapbox && token) {
      Mapbox.setAccessToken(token);
      if (typeof Mapbox.setTelemetryEnabled === 'function') {
        Mapbox.setTelemetryEnabled(false);
      }
    }
  } catch {
    // Mapbox not available
  }
}

function countryNamesToAlpha3(names: string[]): Set<string> {
  const out = new Set<string>();
  for (const n of names) {
    const iso2 = countryDisplayNameToIso2(n);
    if (!iso2) continue;
    const a3 = ISO2_TO_ISO3[iso2];
    if (a3) out.add(a3);
  }
  return out;
}

function mergeBoundsWithPoint(
  bbox: { sw: [number, number]; ne: [number, number] },
  lng: number,
  lat: number
): { sw: [number, number]; ne: [number, number] } {
  return {
    sw: [Math.min(bbox.sw[0], lng), Math.min(bbox.sw[1], lat)],
    ne: [Math.max(bbox.ne[0], lng), Math.max(bbox.ne[1], lat)],
  };
}

/** Improve geocoder hits for common trip destination strings (e.g. "USA"). */
function normalizeCountryForGeocode(country: string): string {
  const t = country.trim();
  if (!t) return '';
  if (/^(usa?|u\.s\.a?\.?|united states of america)$/i.test(t)) return 'United States';
  if (/^us$/i.test(t)) return 'United States';
  if (/^uk$/i.test(t)) return 'United Kingdom';
  if (/^uae$/i.test(t)) return 'United Arab Emirates';
  return t;
}

function buildCityGeocodeQuery(city: string, country: string): string {
  const c = city.trim();
  const cn = normalizeCountryForGeocode(country);
  if (cn) return `${c}, ${cn}`;
  return c;
}

function geocodeResultMatchesCountry(geocodedCountry: string, expectedCountry: string): boolean {
  const expectedIso2 = countryDisplayNameToIso2(normalizeCountryForGeocode(expectedCountry));
  if (!expectedIso2) return true;
  const gotIso2 = countryDisplayNameToIso2(normalizeCountryForGeocode(geocodedCountry));
  if (!gotIso2) return false;
  return gotIso2 === expectedIso2;
}

function isPointNearFeatureCollection(
  lng: number,
  lat: number,
  fc: FeatureCollection,
  paddingDegrees = 6
): boolean {
  const bbox = geoFeatureCollectionBBox(fc);
  if (!bbox) return true;
  return (
    lng >= bbox.sw[0] - paddingDegrees &&
    lng <= bbox.ne[0] + paddingDegrees &&
    lat >= bbox.sw[1] - paddingDegrees &&
    lat <= bbox.ne[1] + paddingDegrees
  );
}

async function resolveCityCoordinates(
  city: string,
  country: string
): Promise<{ latitude: number; longitude: number } | null> {
  const primary = buildCityGeocodeQuery(city, country);
  const expectedCountry = normalizeCountryForGeocode(country);
  const { getDestinationCoordinates } = await import('@/services/weather');

  let res = await getDestinationCoordinates(primary);
  if (res) {
    if (!expectedCountry || geocodeResultMatchesCountry(res.country, expectedCountry)) {
      return { latitude: res.latitude, longitude: res.longitude };
    }
  }

  const fallbackQuery = city.trim();
  if (fallbackQuery && fallbackQuery !== primary && !expectedCountry) {
    res = await getDestinationCoordinates(fallbackQuery);
    if (res) {
      return { latitude: res.latitude, longitude: res.longitude };
    }
  }

  try {
    const { httpsCallable, functions } = await import('@/lib/functions');
    const geocodeLocation = httpsCallable<{ query: string }, Record<string, unknown>>(functions, 'geocodeLocation');
    const cloud = await geocodeLocation({ query: primary });
    const data = cloud?.data as Record<string, unknown> | undefined;
    const cloudCountry =
      typeof data?.country === 'string'
        ? data.country
        : typeof data?.countryName === 'string'
          ? data.countryName
          : '';
    if (data && typeof data.lat === 'number' && typeof data.lon === 'number') {
      if (!expectedCountry || geocodeResultMatchesCountry(cloudCountry, expectedCountry)) {
        return { latitude: data.lat, longitude: data.lon };
      }
    }
    const results = data?.results as Array<{
      lat?: number;
      lon?: number;
      latitude?: number;
      longitude?: number;
      country?: string;
    }> | undefined;
    const first = results?.[0];
    if (first) {
      const lat = first.lat ?? first.latitude;
      const lon = first.lon ?? first.longitude;
      if (typeof lat === 'number' && typeof lon === 'number') {
        if (!expectedCountry || geocodeResultMatchesCountry(first.country ?? cloudCountry, expectedCountry)) {
          return { latitude: lat, longitude: lon };
        }
      }
    }
  } catch {
    // ignore cloud geocode errors
  }

  return null;
}

type Props = {
  visitedCountryNames: string[];
  citiesForPins?: CityPinInput[];
  height?: number;
};

type CityMarker = { key: string; lng: number; lat: number; title: string };

export function VisitedCountriesMap({
  visitedCountryNames,
  citiesForPins = [],
  height = 220,
}: Props) {
  const theme = useTheme();
  const cameraRef = useRef<RNMapboxCamera | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [cityMarkers, setCityMarkers] = useState<CityMarker[]>([]);
  const [geocodingCities, setGeocodingCities] = useState(false);

  const filteredCollection = useMemo(() => {
    const ids = countryNamesToAlpha3(visitedCountryNames);
    if (ids.size === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }
    return {
      type: 'FeatureCollection' as const,
      features: WORLD_GEO.features.filter((f) => f.id != null && ids.has(String(f.id))),
    };
  }, [visitedCountryNames]);

  const countryBbox = useMemo(
    () => geoFeatureCollectionBBox(filteredCollection),
    [filteredCollection]
  );

  const validatedCityMarkers = useMemo(
    () =>
      cityMarkers.filter((m) =>
        isPointNearFeatureCollection(m.lng, m.lat, filteredCollection)
      ),
    [cityMarkers, filteredCollection]
  );

  useEffect(() => {
    if (Platform.OS === 'web' || citiesForPins.length === 0) {
      setCityMarkers([]);
      setGeocodingCities(false);
      return;
    }

    let cancelled = false;
    setGeocodingCities(true);

    const run = async () => {
      const seen = new Set<string>();
      const next: CityMarker[] = [];
      const capped = citiesForPins.slice(0, MAX_CITY_PINS);

      if (capped.length === 0) {
        setCityMarkers([]);
        setGeocodingCities(false);
        return;
      }

      for (const c of capped) {
        if (cancelled) return;
        const city = c.city?.trim();
        const country = c.country?.trim() ?? '';
        if (!city) continue;
        const dedupeKey = buildCityGeocodeQuery(city, country).toLowerCase();
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);

        try {
          const coords = await resolveCityCoordinates(city, country);
          if (cancelled) return;
          if (coords) {
            next.push({
              key: c.key,
              lng: coords.longitude,
              lat: coords.latitude,
              title: city,
            });
          }
        } catch {
          // skip failed geocode
        }

        await new Promise((r) => setTimeout(r, GEOCODE_STAGGER_MS));
      }

      if (!cancelled) {
        setCityMarkers(next);
        setGeocodingCities(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [citiesForPins]);

  const cityPointsCollection = useMemo(() => {
    if (validatedCityMarkers.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] as GeoJSON.Feature[] };
    }
    return {
      type: 'FeatureCollection' as const,
      features: validatedCityMarkers.map((m, index) => ({
        type: 'Feature' as const,
        id: `city-pin-${index}`,
        geometry: {
          type: 'Point' as const,
          coordinates: [m.lng, m.lat],
        },
        properties: { title: m.title },
      })),
    };
  }, [validatedCityMarkers]);

  const fitCameraToBounds = (
    bbox: { sw: [number, number]; ne: [number, number] },
    animationDuration = 450
  ) => {
    if (!cameraRef.current) return;
    cameraRef.current.setCamera({
      bounds: {
        ne: bbox.ne,
        sw: bbox.sw,
        paddingTop: 28,
        paddingRight: 28,
        paddingBottom: 28,
        paddingLeft: 28,
      },
      animationDuration,
    });
  };

  useEffect(() => {
    if (!mapReady || !cameraRef.current) return;

    let bbox = countryBbox;
    for (const m of validatedCityMarkers) {
      if (!bbox) {
        bbox = { sw: [m.lng, m.lat], ne: [m.lng, m.lat] };
      } else {
        bbox = mergeBoundsWithPoint(bbox, m.lng, m.lat);
      }
    }

    if (!bbox) {
      cameraRef.current.setCamera({
        centerCoordinate: [0, 20],
        zoomLevel: 0.9,
        animationDuration: 300,
      });
      return;
    }

    const width = bbox.ne[0] - bbox.sw[0];
    if (width > 160 && countryBbox) {
      fitCameraToBounds(countryBbox, 450);
      return;
    }

    fitCameraToBounds(bbox, 450);
  }, [mapReady, countryBbox, validatedCityMarkers]);

  const initialCameraSettings = useMemo(() => {
    if (countryBbox) {
      const centerLng = (countryBbox.sw[0] + countryBbox.ne[0]) / 2;
      const centerLat = (countryBbox.sw[1] + countryBbox.ne[1]) / 2;
      const lngSpan = countryBbox.ne[0] - countryBbox.sw[0];
      const latSpan = countryBbox.ne[1] - countryBbox.sw[1];
      const span = Math.max(lngSpan, latSpan);
      const zoomLevel = span > 40 ? 2.2 : span > 20 ? 3.2 : span > 8 ? 4.5 : 5.5;
      return {
        centerCoordinate: [centerLng, centerLat] as [number, number],
        zoomLevel,
      };
    }
    return {
      centerCoordinate: [0, 20] as [number, number],
      zoomLevel: 0.9,
    };
  }, [countryBbox]);

  if (Platform.OS === 'web') {
    return (
      <View
        style={[
          styles.fallback,
          { height, backgroundColor: theme.colors.surface, borderColor: theme.colors.borderMuted },
        ]}
      >
        <Caption style={{ color: theme.colors.textSecondary, textAlign: 'center' }}>
          Country map is available on the mobile app.
        </Caption>
      </View>
    );
  }

  if (!Mapbox || !Camera || !ShapeSource || !FillLayer || !CircleLayer) {
    return null;
  }

  if (!isMapboxTokenConfigured()) {
    return (
      <View
        style={[
          styles.fallback,
          { height, backgroundColor: theme.colors.surface, borderColor: theme.colors.borderMuted },
        ]}
      >
        <Caption style={{ color: theme.colors.textSecondary, textAlign: 'center' }}>
          Add a Mapbox token to see your visited countries on the map.
        </Caption>
      </View>
    );
  }

  if (filteredCollection.features.length === 0) {
    return null;
  }

  const CircleLayerComponent = CircleLayer!;

  return (
    <View
      style={[
        styles.wrap,
        {
          height,
          backgroundColor: theme.colors.surface,
          borderColor: theme.colors.borderMuted,
        },
      ]}
    >
      {geocodingCities && cityMarkers.length === 0 ? (
        <View style={styles.geocodeHint}>
          <Caption style={{ color: theme.colors.textTertiary }}>Loading city markers…</Caption>
        </View>
      ) : null}
      {!mapReady ? (
        <View style={styles.loading}>
          <ActivityIndicator color={theme.colors.accent} />
        </View>
      ) : null}
      <Mapbox.MapView
        style={styles.map}
        styleURL={MAPBOX_STYLE_URL}
        scaleBarEnabled={false}
        logoEnabled={false}
        attributionEnabled={true}
        onDidFinishLoadingMap={() => setMapReady(true)}
        onDidFinishLoadingStyle={() => setMapReady(true)}
      >
        <Camera
          ref={cameraRef}
          defaultSettings={initialCameraSettings}
        />
        <ShapeSource id="visitedCountries" shape={filteredCollection}>
          <FillLayer
            id="visitedCountriesFill"
            style={{
              fillColor: theme.colors.accent,
              fillOpacity: 0.45,
              fillOutlineColor: 'rgba(0,0,0,0)',
            }}
          />
        </ShapeSource>
        {cityPointsCollection.features.length > 0 ? (
          <ShapeSource id="visitedCityPins" shape={cityPointsCollection}>
            <CircleLayerComponent
              id="visitedCityPinsCircle"
              style={{
                circleRadius: 7,
                circleColor: theme.colors.accent,
                circleOpacity: 1,
                circleStrokeWidth: 2,
                circleStrokeColor: theme.colors.surface,
              }}
            />
          </ShapeSource>
        ) : null}
      </Mapbox.MapView>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 16,
    borderWidth: StyleSheet.hairlineWidth,
  },
  map: {
    flex: 1,
  },
  loading: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  geocodeHint: {
    position: 'absolute',
    top: 8,
    left: 0,
    right: 0,
    zIndex: 2,
    alignItems: 'center',
  },
  fallback: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    marginBottom: 16,
    borderWidth: StyleSheet.hairlineWidth,
  },
});
